import { useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStartPublicSession } from "./use-start-public-session";
import i18n from "@/features/utils/i18n";

/**
 * The conversation's id — and the fact that it might not exist yet.
 *
 * **Opening the widget is not starting a conversation.** It used to be: the iframe
 * landed on `/c/new`, which created a session and redirected to it, so every visitor
 * who opened the panel and read for a moment left a row behind. Those rows were then
 * swept up two minutes later by the stale-session cron and wrapped up — which is where
 * the untitled, `failure` / `negative` conversations came from, in bulk.
 *
 * So nothing is created until somebody says something. The chat renders from the
 * connector until then (`usePublicConnectorInfo`), and the first thing that genuinely
 * needs a session — a message, or picking up the phone — calls `ensureSession` and gets
 * one. What the widget shows does not change at that moment; only the URL does.
 *
 * Two details are load-bearing:
 *  - **single-flight.** A visitor who starts the call and types in the same breath must
 *    not create two conversations, and neither must React running an effect twice. The
 *    in-flight promise is the lock, and it is held for the whole creation.
 *  - **`replace`, not `push`.** The creation URL is not a place to go back to: `back`
 *    from `/c/<id>` must leave the chat, not land on a `/c/new` that would create a
 *    second conversation on sight.
 */
export interface SessionHandle {
  /** The conversation, once there is one. */
  publicId: string | undefined;
  /** Create it if it does not exist yet, and hand back its id either way. */
  ensureSession: () => Promise<string>;
}

export function useSessionHandle(
  routeId: string | undefined,
): SessionHandle {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const start = useStartPublicSession();

  // "new" is the creation route, not an id.
  const publicId = routeId && routeId !== "new" ? routeId : undefined;

  const pending = useRef<Promise<string> | null>(null);
  // The id is also kept outside React state: `ensureSession` may be called twice before
  // the navigation it triggers has re-rendered anything.
  const created = useRef<string | null>(null);

  const ensureSession = useCallback(async (): Promise<string> => {
    if (publicId) return publicId;
    if (created.current) return created.current;
    if (pending.current) return pending.current;

    const connector = params.get("connector");
    if (!connector) {
      throw new Error(i18n.t("chat.missing_connector", { ns: "public" }));
    }

    const userHash = params.get("user_hash") || undefined;
    const userEmail = params.get("user_email") || undefined;
    const userPhone = params.get("user_phone") || undefined;
    // A contact the merchant signed: `/start` re-computes the HMAC and only then
    // trusts it. Unsigned details are applied afterwards, at the trust level of
    // something the visitor typed (see use-embed-intent.ts).
    const verified = !!((userEmail || userPhone) && userHash);

    pending.current = start
      .mutateAsync({
        connector,
        language: params.get("lang") || undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        origin: params.get("origin") || undefined,
        device: params.get("device") || undefined,
        user: verified
          ? {
              hash: userHash!,
              email: userEmail,
              phone: userPhone,
              given_name: params.get("user_name") || undefined,
            }
          : undefined,
      })
      .then(({ public_id }) => {
        created.current = public_id;
        // Same route, new parameter: the page is not remounted, so nothing the visitor
        // has on screen — or half-typed — is lost.
        navigate(`/c/${public_id}?${params.toString()}`, { replace: true });
        return public_id;
      })
      .finally(() => {
        pending.current = null;
      });

    return pending.current;
  }, [publicId, params, navigate, start]);

  return { publicId, ensureSession };
}
